import { minutes, units as makeUnits, type Minutes, type Units } from '../domain/branded.js';
import { InvalidArgumentError } from '../domain/errors.js';
import type { TaskType } from '../domain/task.js';

/**
 * 工期 / 工時 / 單位（duration / work / units）三者的鎖定關係。
 *
 * 恆等式：`work = duration × units`（units 為全部指派的合計投入單位）。
 * 任一項被使用者改動時，由 `taskType` 決定「哪一項固定、哪一項被重算」：
 *
 * | taskType      | 改 duration      | 改 work          | 改 units         |
 * |---------------|------------------|------------------|------------------|
 * | FixedUnits    | work 重算        | duration 重算    | duration 重算    |
 * | FixedDuration | （duration 即輸入）work 重算 | units 重算 | work 重算        |
 * | FixedWork     | units 重算       | duration 重算    | duration 重算    |
 *
 * `effortDriven` 只在「改 units」時生效：為 true 時 work 視為固定，
 * 因此改變投入單位會改變工期而非工時（MS Project 的工作導向語義）。
 * FixedWork 隱含 effortDriven = true。
 */
export interface TaskEffort {
  readonly durationMinutes: Minutes;
  readonly work: Minutes;
  readonly units: Units;
}

/** 使用者這次改動的是哪一項。 */
export type EffortChange = 'duration' | 'work' | 'units';

const positive = (value: number, label: string): number => {
  if (!Number.isFinite(value) || value <= 0) {
    throw new InvalidArgumentError(`recalculateEffort(): ${label} must be a positive finite number`);
  }
  return value;
};

const nonNegative = (value: number, label: string): number => {
  if (!Number.isFinite(value) || value < 0) {
    throw new InvalidArgumentError(`recalculateEffort(): ${label} must be a non-negative finite number`);
  }
  return value;
};

/**
 * 依 taskType 重新平衡 duration / work / units。
 *
 * `next` 是使用者輸入後的新值（三項都要帶，只有 `changed` 指定的那一項被視為權威輸入，
 * 其餘兩項會依規則保留或重算）。回傳的三項恆滿足 `work = duration × units`。
 *
 * 里程碑（duration = 0）不做換算，直接回傳輸入值。
 */
export function recalculateEffort(
  taskType: TaskType,
  effortDriven: boolean,
  next: TaskEffort,
  changed: EffortChange,
): TaskEffort {
  const duration = nonNegative(next.durationMinutes as number, 'durationMinutes');
  const work = nonNegative(next.work as number, 'work');
  const unitCount = nonNegative(next.units as number, 'units');

  // 里程碑 / 無工時任務：沒有可解的換算關係，原樣回傳。
  if (duration === 0 || unitCount === 0) {
    return { durationMinutes: minutes(duration), work: minutes(work), units: makeUnits(unitCount) };
  }

  const effortIsFixed = taskType === 'FixedWork' || effortDriven;

  switch (changed) {
    case 'duration': {
      if (taskType === 'FixedWork') {
        // 工時固定 → 由新工期反推單位。
        return {
          durationMinutes: minutes(duration),
          work: minutes(work),
          units: makeUnits(work / positive(duration, 'durationMinutes')),
        };
      }
      // FixedUnits / FixedDuration：單位維持，工時隨工期變動。
      return {
        durationMinutes: minutes(duration),
        work: minutes(duration * unitCount),
        units: makeUnits(unitCount),
      };
    }
    case 'work': {
      if (taskType === 'FixedDuration') {
        // 工期固定 → 由新工時反推單位。
        return {
          durationMinutes: minutes(duration),
          work: minutes(work),
          units: makeUnits(work / positive(duration, 'durationMinutes')),
        };
      }
      // FixedUnits / FixedWork：單位維持，工期隨工時變動。
      return {
        durationMinutes: minutes(work / positive(unitCount, 'units')),
        work: minutes(work),
        units: makeUnits(unitCount),
      };
    }
    case 'units': {
      if (taskType === 'FixedDuration') {
        // 工期固定：effortDriven 時工時不變（無解則以工期×單位重算），否則工時隨單位變動。
        if (effortIsFixed) {
          return {
            durationMinutes: minutes(duration),
            work: minutes(work),
            units: makeUnits(work / positive(duration, 'durationMinutes')),
          };
        }
        return {
          durationMinutes: minutes(duration),
          work: minutes(duration * unitCount),
          units: makeUnits(unitCount),
        };
      }
      if (effortIsFixed) {
        // 工時固定 → 增加單位會縮短工期。
        return {
          durationMinutes: minutes(work / positive(unitCount, 'units')),
          work: minutes(work),
          units: makeUnits(unitCount),
        };
      }
      // FixedUnits 且非工作導向：工期不變，工時隨單位變動。
      return {
        durationMinutes: minutes(duration),
        work: minutes(duration * unitCount),
        units: makeUnits(unitCount),
      };
    }
    default: {
      const exhaustive: never = changed;
      throw new InvalidArgumentError(`recalculateEffort(): unknown change ${String(exhaustive)}`);
    }
  }
}
